import React from 'react';
import type { UploadChangeParam } from 'antd/es/upload';
import { message, Spin, Upload, Button } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { uploadFilePrivate } from 'api/common';
import UploadIcon from './upload.svg';
import PdfIcon from './pdf.png';
import s from './s.module.less';
import { getUserId } from 'utils/localstore';
import { getPrivateFilePreview } from 'api/ehr';
import { getPrivateFilePreview as getPrivateFilePreviewOperation } from 'api/operation';
import clsx from 'classnames';

const { Dragger } = Upload;

type FileUploadProps = {
    isOps?: boolean;
    value?: string;
    tips?: string;
    accept?: string;
    onChange?: (val: string) => void;
    onChangeFullUrl?: (fullUrl: string) => void;
    uploadType?: 'PROSPECT_INSURANCE' | 'PROSPECT_IDENTITY' | 'IMAGE' | 'LICENSE';
    title?: string | React.ReactNode;
    previewMode?: 'horizontal' | 'vertical';
    notNeedSign?: boolean;
    previewUrl?: string; // 已获取的预览 URL，如果提供则直接使用，不调用接口
};

const FileUploadPrivate = (Props: FileUploadProps) => {
    const {
        isOps,
        accept,
        value,
        tips,
        onChange,
        onChangeFullUrl,
        uploadType = 'IMAGE',
        title,
        previewMode = 'horizontal',
        notNeedSign,
        previewUrl: externalPreviewUrl,
    } = Props;
    const [loading, setLoading] = React.useState(false);
    // Display name of the uploaded file, so a PDF shows its filename instead of a broken image.
    const [fileName, setFileName] = React.useState<string>('');

    const fileInfoRef = React.useRef<UploadChangeParam<UploadFile>>();
    const [showUploadList, setShowUploadList] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState<string | undefined>(undefined);
    // 记录已换取过签名 URL 的 value：上传流程里已调过 getPreview 时，避免 value 变化触发的 effect 重复请求
    const previewedValueRef = React.useRef<string | undefined>(undefined);
    let acceptTypeString = '*';
    if (uploadType === 'LICENSE') {
        acceptTypeString = 'image/*,application/pdf';
    }

    const handleChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        fileInfoRef.current = info;
        if (info.file.status === 'uploading') {
            setLoading(true);
        }
    };

    const getPreview = async (v?: string) => {
        if (v) {
            previewedValueRef.current = v;
            if (v.includes('http')) {
                setPreviewUrl(v);
                return v;
            }
            try {
                const fn = isOps ? getPrivateFilePreviewOperation : getPrivateFilePreview;
                const res = await fn(v);
                const signedUrl = res.data?.data?.signedUrl || res.data?.data?.singedUrl;
                setPreviewUrl(signedUrl);
                return signedUrl;
            } catch (e) {
                console.error(e);
            }
        }
    };

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        onChange: handleChange,
        accept: accept || acceptTypeString,
        showUploadList,
        beforeUpload(file) {
            return new Promise((resolve, reject) => {
                const isLtSize = file.size / 1024 / 1024 <= 30;
                if (!isLtSize) {
                    message.error('File size cannot exceed 30MB. Please choose a smaller file.');
                    reject(false);
                } else {
                    resolve(true);
                }
            });
        },
        onDrop() {
            //console.log('Dropped files', e.dataTransfer.files);
        },
        customRequest: async (data) => {
            //https://github.com/react-component/upload#customrequest
            try {
                const formData = new FormData();
                formData.append('file', data.file);
                formData.append('uploadType', uploadType);
                formData.append('providerId', getUserId()?.toString() ?? '');
                const res = await uploadFilePrivate(formData);
                if (!res.error) {
                    if (fileInfoRef.current) {
                        fileInfoRef.current.file.status = 'done';
                    }
                    setShowUploadList(true);
                    const url = res.data?.data;
                    setFileName(fileInfoRef.current?.file?.name ?? '');
                    onChange?.(url);
                    const fullUrl = await getPreview(url);
                    onChangeFullUrl?.(fullUrl);
                }
            } catch (e) {
                setShowUploadList(false);
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
    };

    React.useEffect(() => {
        // 如果提供了外部预览 URL，优先使用，不调用接口
        if (externalPreviewUrl) {
            setPreviewUrl(externalPreviewUrl);
            return;
        }

        if (value) {
            // Keep the friendly name set during upload; otherwise fall back to the key's basename.
            const basename = value.split('/').pop()?.split('?')[0] ?? '';
            setFileName((prev) => prev || basename);
            if (notNeedSign) {
                setPreviewUrl(value);
                return;
            }
            // 上传流程已为该 value 取过签名 URL，跳过避免重复调用 signed 接口
            if (value === previewedValueRef.current) {
                return;
            }
            getPreview(value);
        } else {
            setPreviewUrl(undefined);
            setFileName('');
            previewedValueRef.current = undefined;
        }
    }, [value, notNeedSign, externalPreviewUrl]);

    // PDFs can't render in an <img>, so preview them as an icon + filename link instead.
    const isPreviewPdf = (fileName || value || '').toLowerCase().endsWith('pdf');

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                {!value && (
                    <Dragger {...props}>
                        <div className="px-4">
                            {title && <p className={s.title}>{title}</p>}
                            <p className={s.iconWrap}>
                                <img src={UploadIcon} className={s.icon} />
                            </p>
                            <p className={s.uploadText}>
                                <span>Upload a file</span> or drag and drop
                            </p>
                            <p className={s.uploadHint}>{tips ?? 'PNG, JPG, PDF up to 10MB'}</p>
                        </div>
                    </Dragger>
                )}
            </Spin>
            {previewUrl && (
                <div className={clsx(s.preview, s[previewMode])}>
                    {isPreviewPdf ? (
                        // PDF 不能用 <img> 渲染，只显示图标 + 文件名；点击用签名 URL 新开窗口预览
                        <a
                            className={s.pdfPreview}
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={fileName || 'Document.pdf'}
                        >
                            <img src={PdfIcon} alt="pdf" className={s.pdfIcon} />
                            <span className={s.pdfName}>{fileName || 'Document.pdf'}</span>
                        </a>
                    ) : (
                        <div className={s.previewImgWrap}>
                            <img src={previewUrl} alt="preview" className={s.previewImg} />
                        </div>
                    )}
                    <Upload {...props} showUploadList={false}>
                        <Button className={s.changeBtn}>Change</Button>
                    </Upload>
                </div>
            )}
        </div>
    );
};

export default FileUploadPrivate;
